import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PressSetupPanel from './PressSetupPanel';
import { getActiveLocations } from '../utils/pressSetup';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    backgroundColor: 'var(--al-surface)',
    color: 'white',
    borderRadius: 0,
    minWidth: '780px',
    maxWidth: '1080px',
    border: '1px solid rgba(var(--al-primary-rgb), 0.3)'
  }
});

const StyledDialogTitle = styled(DialogTitle)({
  backgroundColor: 'var(--al-bg)',
  color: 'var(--al-primary)',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 700,
  fontSize: '0.9rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
});

const StyledDialogContent = styled(DialogContent)({
  padding: '28px 32px',
  backgroundColor: 'var(--al-surface)',
  textTransform: 'uppercase',
  fontFamily: "'Montserrat', sans-serif"
});

const StyledDialogActions = styled(DialogActions)({
  backgroundColor: 'var(--al-bg)',
  padding: '12px 20px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  gap: '8px'
});

const SaveButton = styled(Button)({
  backgroundColor: 'var(--al-primary)',
  color: 'var(--al-bg)',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 20px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  '&:hover': { backgroundColor: '#7FE87F' },
  '&:disabled': {
    backgroundColor: 'rgba(var(--al-primary-rgb), 0.3)',
    color: 'rgba(0, 0, 0, 0.5)'
  }
});

const CancelButton = styled(Button)({
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 16px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
});

const PressSetupModal = ({ isOpen, onClose, lineData, pressSetup, onSave, designName, inkCatalog = [] }) => {
  const activeLocations = getActiveLocations(lineData);
  const [pendingSave, setPendingSave] = React.useState(null);

  React.useEffect(() => {
    if (isOpen) {
      setPendingSave(null);
    }
  }, [isOpen, lineData?.id]);

  const handleSave = () => {
    if (pendingSave) {
      onSave(pendingSave);
    }
    onClose();
  };

  if (!isOpen) return null;

  if (activeLocations.length === 0) {
    return (
      <StyledDialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <span>
            Press Setup{designName ? ` — ${String(designName).toUpperCase()}` : ''}
          </span>
          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', textTransform: 'uppercase' }}>
            Add ink colors to a print location before configuring press setup.
          </Typography>
        </StyledDialogContent>
        <StyledDialogActions>
          <CancelButton onClick={onClose}>Close</CancelButton>
        </StyledDialogActions>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <span>
          Press Setup{designName ? ` — ${String(designName).toUpperCase()}` : ''}
        </span>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <PressSetupPanel
          lineData={lineData}
          pressSetup={pressSetup}
          inkCatalog={inkCatalog}
          onPressSetupChange={setPendingSave}
          compact={false}
          showPressNotes
        />
      </StyledDialogContent>

      <StyledDialogActions>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <SaveButton onClick={handleSave}>
          Save Setup
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default PressSetupModal;
